import React, { useMemo } from 'react';
import { BarChart } from '@mantine/charts';

// Example interface for a rent contract
interface RentContract {
    id: number;
    startDate: string; // or Date
    endDate: string;   // or Date
    // other fields...
}


// Helper function to get the next 6 months (including current)
function getNextSixMonths(): Date[] {
    const months: Date[] = [];
    const now = new Date();

    // Start from the beginning of the current month
    const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    for (let i = 0; i < 6; i++) {
        // Create a new date for each month
        const d = new Date(startOfThisMonth);
        d.setMonth(startOfThisMonth.getMonth() + i);
        months.push(d);
    }
    return months;
}

// Function to get a display label like "Jan 2025"
function getMonthLabel(d: Date): string {
    return d.toLocaleString('default', { month: 'short', year: 'numeric' });
}

// Our main component
interface ContractsBarChartProps {
    contracts: RentContract[];
}

export function ContractsBarChart({ contracts }: ContractsBarChartProps) {
    // Build the data for the next 6 months
    const chartData = useMemo(() => {
        // 1) Generate an initial structure for the next 6 months
        const sixMonths = getNextSixMonths();

        const extendedData = sixMonths.map((d) => ({
            month: getMonthLabel(d),
            starts: 0,
            ends: 0,
            dateObj: d,
        }));

        // Now we can compare startDate's month/year with extendedData[i].dateObj
        contracts.forEach((contract) => {
            const start = new Date(contract.startDate);
            const end = new Date(contract.endDate);

            extendedData.forEach((mItem) => {
                const mYear = mItem.dateObj.getFullYear();
                const mMonth = mItem.dateObj.getMonth();

                // If contract starts in that month/year
                if (start.getFullYear() === mYear && start.getMonth() === mMonth) {
                    mItem.starts += 1;
                }
                // If contract ends in that month/year
                if (end.getFullYear() === mYear && end.getMonth() === mMonth) {
                    mItem.ends += 1;
                }
            });
        });

        // Finally, remove the 'dateObj' since the chart only needs 'month', 'starts', 'ends'
        return extendedData.map(({ dateObj, ...rest }) => rest);
    }, [contracts]);

    // Now we can render the BarChart
    return (
        <BarChart
            h={300}

            data={chartData}  // e.g. [ { month: 'Jan 2025', starts: 2, ends: 1 }, ... ]
            dataKey="month"
            yAxisLabel="Anzahl"
            series={[
                { name: 'starts', color: 'green.6', label: 'Beginnen'},
                { name: 'ends', color: 'red.6', label: 'Enden' },
            ]}
            tickLine="y"
        />
    );
}
